import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import BoardResizer from '../BoardResizer/BoardResizer'
import './ChessBoard.css'

// --- Lazy Load Images (Full Set) ---
const loadPieceImages = (theme) => {
  try {
    if (theme === 'cyberpunk') {
      return {
        'K': require('../../assets/images/pieces/cyberpunk/kingwhite.png'),
        'Q': require('../../assets/images/pieces/cyberpunk/queenwhite.png'),
        'R': require('../../assets/images/pieces/cyberpunk/rookwhite.png'),
        'B': require('../../assets/images/pieces/cyberpunk/bishopwhite.png'),
        'N': require('../../assets/images/pieces/cyberpunk/knightwhite.png'),
        'P': require('../../assets/images/pieces/cyberpunk/pawnwhite.png'),
        'k': require('../../assets/images/pieces/cyberpunk/kingblack.png'),
        'q': require('../../assets/images/pieces/cyberpunk/queenblack.png'),
        'r': require('../../assets/images/pieces/cyberpunk/rookblack.png'),
        'b': require('../../assets/images/pieces/cyberpunk/bishopblack.png'),
        'n': require('../../assets/images/pieces/cyberpunk/knightblack.png'),
        'p': require('../../assets/images/pieces/cyberpunk/pawnblack.png')
      }
    }
    if (theme === '2') {
      return {
        'K': require('../../assets/images/pieces/2/whiteking.png'),
        'Q': require('../../assets/images/pieces/2/whitequeen.png'),
        'R': require('../../assets/images/pieces/2/whiterook.png'),
        'B': require('../../assets/images/pieces/2/whitebishop.png'),
        'N': require('../../assets/images/pieces/2/whiteknight.png'),
        'P': require('../../assets/images/pieces/2/whitepawn.png'),
        'k': require('../../assets/images/pieces/2/blackking.png'),
        'q': require('../../assets/images/pieces/2/blackqueen.png'),
        'r': require('../../assets/images/pieces/2/blackrook.png'),
        'b': require('../../assets/images/pieces/2/blackbishop.png'),
        'n': require('../../assets/images/pieces/2/blackknight.png'),
        'p': require('../../assets/images/pieces/2/blackpawn.png')
      }
    }
    if (theme === '3') {
      return {
        'K': require('../../assets/images/pieces/3/whiteking.png'),
        'Q': require('../../assets/images/pieces/3/whitequeen.png'),
        'R': require('../../assets/images/pieces/3/whiterook.png'),
        'B': require('../../assets/images/pieces/3/whitebishop.png'),
        'N': require('../../assets/images/pieces/3/whiteknight.png'),
        'P': require('../../assets/images/pieces/3/whitepawn.png'),
        'k': require('../../assets/images/pieces/3/blackking.png'),
        'q': require('../../assets/images/pieces/3/blackqueen.png'),
        'r': require('../../assets/images/pieces/3/blackrook.png'),
        'b': require('../../assets/images/pieces/3/blackbishop.png'),
        'n': require('../../assets/images/pieces/3/blackknight.png'),
        'p': require('../../assets/images/pieces/3/blackpawn.png')
      }
    }
    // Default to classic theme
    return {
      'K': require('../../assets/images/pieces/classic/white-king.png'),
      'Q': require('../../assets/images/pieces/classic/white-queen.png'),
      'R': require('../../assets/images/pieces/classic/white-rook.png'),
      'B': require('../../assets/images/pieces/classic/white-bishop.png'),
      'N': require('../../assets/images/pieces/classic/white-knight.png'),
      'P': require('../../assets/images/pieces/classic/white-pawn.png'),
      'k': require('../../assets/images/pieces/classic/black-king.png'),
      'q': require('../../assets/images/pieces/classic/black-queen.png'),
      'r': require('../../assets/images/pieces/classic/black-rook.png'),
      'b': require('../../assets/images/pieces/classic/black-bishop.png'),
      'n': require('../../assets/images/pieces/classic/black-knight.png'),
      'p': require('../../assets/images/pieces/classic/black-pawn.png')
    }
  } catch (error) {
    console.error(`Failed to load ${theme} piece images:`, error)
    return {}
  }
}

// --- Theme Constants (Preserved) ---
const BOARD_THEMES = {
  classic: {
    lightSquare: '#f0d9b5',
    darkSquare: '#b58863'
  },
  green: {
    lightSquare: '#eeeed2',
    darkSquare: '#769656'
  },
  blue: {
    lightSquare: '#e8f4f8',
    darkSquare: '#4a90a4'
  },
  riftchess: {
    lightSquare: '#2d2d2d',
    darkSquare: '#1a1a1a'
  },
  neon: {
    lightSquare: '#1a1a2e',
    darkSquare: '#0f3460'
  },
  grey: {
    lightSquare: '#f5f5f5',
    darkSquare: '#999999'
  }
}

// --- Refactored Hook: useGhostPiece ---
const useGhostPiece = (isActive, boardRef) => {
    const [position, setPosition] = useState(null)

    useEffect(() => {
        if (!isActive || !boardRef.current) {
            setPosition(null)
            return
        }
        const el = boardRef.current
        const handleMove = (e) => {
            const rect = el.getBoundingClientRect()
            setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top })
        }
        const handleLeave = () => setPosition(null)

        el.addEventListener('mousemove', handleMove)
        el.addEventListener('mouseleave', handleLeave)
        return () => {
            el.removeEventListener('mousemove', handleMove)
            el.removeEventListener('mouseleave', handleLeave)
        }
    }, [isActive, boardRef])

    return position
}

const ChessBoard = ({
  game,
  lastMove: propLastMove,
  onMove,
  onSquareClick,
  selectedSquare,
  legalMoves = [],
  isPlayerTurn = false,
  onPieceSelect,
  onPieceDeselect,
  orientation = 'white',
  theme = 'classic',
  boardTheme = 'riftchess',
  teleportTargets = [],
  illegalTeleportSquares = [],
  teleportGhostPiece = null,
  gameResult = null,
  gameStatus = null,
  currentTurn = null,
  isPlayerInGame = false,
  isCurrentPlayerTurn = false,
  topPlayer = null,
  bottomPlayer = null,
  animatingMove = null,
  allowResize = true,
  showAnnotations = false
}) => {
  const pieceImages = useMemo(() => loadPieceImages(theme), [theme])
  const { user } = useAuth()
  const boardRef = useRef(null)
  
  // --- Hooks and State ---
  const ghostPosition = useGhostPiece(!!teleportGhostPiece, boardRef)
  
  const [draggedPiece, setDraggedPiece] = useState(null)
  const [draggedFrom, setDraggedFrom] = useState(null)
  const [boardSize, setBoardSize] = useState(400)

  // Animation state
  const [internalAnimatingMove, setInternalAnimatingMove] = useState(null)
  const [animatedPiecePosition, setAnimatedPiecePosition] = useState(null)
  const prevLastMoveRef = useRef(null)
  const animationTimeoutRef = useRef(null)

  // Annotation state
  const [annotations, setAnnotations] = useState({ arrows: [], circles: [] })
  const [isRightClicking, setIsRightClicking] = useState(false)
  const [rightClickStart, setRightClickStart] = useState(null)
  const [previewArrow, setPreviewArrow] = useState(null)

  // --- Optimization: Animation Logic (100ms) ---
  useEffect(() => {
    if (propLastMove && propLastMove !== prevLastMoveRef.current) {
      const isNewMove = prevLastMoveRef.current?.from !== propLastMove.from || 
                        prevLastMoveRef.current?.to !== propLastMove.to

      if (isNewMove && propLastMove.from && propLastMove.to && propLastMove.piece) {
          let shouldAnimate = true
          
          if (isPlayerInGame && user) {
            const piece = propLastMove.piece
            const isWhitePiece = piece === piece.toUpperCase()
            const isUserWhite = game?.white_player_id === user.id
            const isUserBlack = game?.black_player_id === user.id

            if ((isWhitePiece && isUserWhite) || (!isWhitePiece && isUserBlack)) {
              shouldAnimate = false
            }
          }

          if (shouldAnimate) {
             setInternalAnimatingMove({ ...propLastMove, startTime: Date.now() })

             if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current)

             // Buffer reduced to 100ms to exactly match CSS transition for snappier feel
             animationTimeoutRef.current = setTimeout(() => {
               setInternalAnimatingMove(null)
             }, 100) 
          }
      }
    }
    prevLastMoveRef.current = propLastMove
  }, [propLastMove, isPlayerInGame, user, game?.white_player_id, game?.black_player_id])

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current)
    }
  }, [])

  const activeAnimatingMove = animatingMove || internalAnimatingMove

  // --- Helpers ---
  const squareToCoords = useCallback((square) => {
    if (!square) return [0, 0]
    const file = square.charCodeAt(0) - 'a'.charCodeAt(0)
    const rank = 8 - parseInt(square[1])
    return [rank, file]
  }, [])

  const coordsToSquare = useCallback((rank, file) => {
    return String.fromCharCode('a'.charCodeAt(0) + file) + (8 - rank)
  }, [])

  const calculateSquarePosition = useCallback((square) => {
    const [rank, file] = squareToCoords(square)
    const displayRank = orientation === 'white' ? rank : (7 - rank)
    const displayFile = orientation === 'white' ? file : (7 - file)

    return {
      left: `${displayFile * 12.5}%`,
      top: `${displayRank * 12.5}%`,
      width: '12.5%',
      height: '12.5%'
    }
  }, [orientation, squareToCoords])

  // Coordinate Animation Effect
  useEffect(() => {
    if (!activeAnimatingMove) {
      setAnimatedPiecePosition(null)
      return
    }
    const fromPos = calculateSquarePosition(activeAnimatingMove.from)
    const toPos = calculateSquarePosition(activeAnimatingMove.to)

    if (!fromPos || !toPos) {
      setAnimatedPiecePosition(null)
      return
    }

    setAnimatedPiecePosition({
      piece: activeAnimatingMove.piece,
      ...fromPos,
      targetLeft: toPos.left,
      targetTop: toPos.top,
      animating: true
    })

    const animationTimer = setTimeout(() => {
      setAnimatedPiecePosition(prev => prev ? { ...prev, left: toPos.left, top: toPos.top } : null)
    }, 10) // Small tick to trigger CSS

    return () => clearTimeout(animationTimer)
  }, [activeAnimatingMove, calculateSquarePosition])

  // --- Board Parsing & Special Squares (Consolidated) ---
  const board = useMemo(() => {
    const fen = game?.board
    if (!fen) return Array(8).fill().map(() => Array(8).fill(''))
    if (Array.isArray(fen)) return fen

    const parts = fen.split(' ')
    const rows = parts[0].split('/')
    return rows.map(row => {
      let rank = []
      for (let char of row) {
        if (/\d/.test(char)) {
          rank.push(...Array(parseInt(char)).fill(''))
        } else {
          rank.push(char)
        }
      }
      return rank
    })
  }, [game?.board])

  const parseSquares = useCallback((json, moveCount) => {
      if (!json) return []
      try {
          const squares = JSON.parse(json)
          if (!moveCount && moveCount !== 0) return squares
          return squares.filter(s => {
              const expire = s.expires_after_move
              // Keep if no expiry defined or if move count is within expiry limit
              // Note: Use weak comparison matching user logic or strict check?
              // User logic: typeof rawExpire === 'number' ? rawExpire : parseInt...
              // We'll trust basic strict comparison for refactor cleanliness, assuming standardized backend data
              return expire === undefined || expire === null || moveCount <= expire
          })
      } catch { return [] }
  }, [])

  const specialSquares = useMemo(() => {
      const moveCount = typeof game?.move_count === 'number' ? game.move_count : null
      return {
          red: parseSquares(game?.red_squares, moveCount),
          blue: parseSquares(game?.blue_squares, moveCount),
          green: parseSquares(game?.green_squares, moveCount)
      }
  }, [game?.red_squares, game?.blue_squares, game?.green_squares, game?.move_count, parseSquares])

  const visualEffects = useMemo(() => {
      if (!game?.visual_effects) return []
      try {
          const effects = JSON.parse(game.visual_effects)
          const now = Date.now() / 1000
          if (!Array.isArray(effects)) return []
          return effects.filter(e => {
             const timeValid = !e.expires_at || e.expires_at > now
             const moveValid = !e.expires_after_move || (typeof game.move_count === 'number' && game.move_count < e.expires_after_move)
             return timeValid && moveValid
          })
      } catch { return [] }
  }, [game?.visual_effects, game?.move_count])

  // --- Attack Logic (Preserved) ---
  const canPawnAttackSquare = useCallback((fromRank, fromFile, toRank, toFile, isWhite) => {
    const fileDiff = Math.abs(fromFile - toFile)
    const rankDiff = toRank - fromRank
    if (fileDiff !== 1) return false
    return isWhite ? rankDiff === -1 : rankDiff === 1
  }, [])

  const isValidKnightMove = useCallback((fromRank, fromFile, toRank, toFile) => {
    const rankDiff = Math.abs(toRank - fromRank)
    const fileDiff = Math.abs(toFile - fromFile)
    return (rankDiff === 2 && fileDiff === 1) || (rankDiff === 1 && fileDiff === 2)
  }, [])

  const canKingAttackSquare = useCallback((fromRank, fromFile, toRank, toFile) => {
    const rankDiff = Math.abs(fromRank - toRank)
    const fileDiff = Math.abs(fromFile - toFile)
    return rankDiff <= 1 && fileDiff <= 1 && !(rankDiff === 0 && fileDiff === 0)
  }, [])

  const isPathClear = useCallback((board, fromRank, fromFile, toRank, toFile) => {
    const rankStep = toRank > fromRank ? 1 : toRank < fromRank ? -1 : 0
    const fileStep = toFile > fromFile ? 1 : toFile < fromFile ? -1 : 0
    let currentRank = fromRank + rankStep
    let currentFile = fromFile + fileStep
    while (currentRank !== toRank || currentFile !== toFile) {
      if (board[currentRank][currentFile]) return false
      currentRank += rankStep
      currentFile += fileStep
    }
    return true
  }, [])

  const canPieceAttackSquare = useCallback((board, fromRank, fromFile, toRank, toFile) => {
    const piece = board[fromRank][fromFile]
    if (!piece) return false
    const pieceType = piece.toLowerCase()
    const isWhite = piece === piece.toUpperCase()
    
    // Simplified router
    if (pieceType === 'p') return canPawnAttackSquare(fromRank, fromFile, toRank, toFile, isWhite)
    if (pieceType === 'n') return isValidKnightMove(fromRank, fromFile, toRank, toFile)
    if (pieceType === 'k') return canKingAttackSquare(fromRank, fromFile, toRank, toFile)
    
    // Sliding pieces
    if (pieceType === 'r' || pieceType === 'q') {
       if ((fromRank === toRank || fromFile === toFile) && isPathClear(board, fromRank, fromFile, toRank, toFile)) return true
    }
    if (pieceType === 'b' || pieceType === 'q') {
       if (Math.abs(toRank - fromRank) === Math.abs(toFile - fromFile) && isPathClear(board, fromRank, fromFile, toRank, toFile)) return true
    }
    return false
  }, [canPawnAttackSquare, isValidKnightMove, canKingAttackSquare, isPathClear])

  const isSquareUnderAttack = useCallback((board, targetRank, targetFile, byWhite) => {
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file]
        if (!piece) continue
        const isPieceWhite = piece === piece.toUpperCase()
        if (isPieceWhite !== byWhite) continue
        if (canPieceAttackSquare(board, rank, file, targetRank, targetFile)) return true
      }
    }
    return false
  }, [canPieceAttackSquare])

  const isKingInCheck = useCallback((board, isWhite) => {
    let kingRank = -1, kingFile = -1
    const kingChar = isWhite ? 'K' : 'k'
    // Find King
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        if (board[rank][file] === kingChar) {
          kingRank = rank; kingFile = file; break
        }
      }
      if (kingRank !== -1) break
    }
    if (kingRank === -1) return false
    return isSquareUnderAttack(board, kingRank, kingFile, !isWhite)
  }, [isSquareUnderAttack])

  // --- Interaction Handlers ---
  const handleDragStart = (e, rank, file) => {
    const piece = board[rank][file]
    if (!piece) { e.preventDefault(); return }

    const isWhitePiece = piece === piece.toUpperCase()
    const isPlayerWhite = game?.white_player_id === user?.id
    if ((isPlayerWhite && !isWhitePiece) || (!isPlayerWhite && isWhitePiece)) {
      e.preventDefault(); return
    }
    if (!isPlayerTurn) return

    const square = coordsToSquare(rank, file)
    setDraggedPiece(piece)
    setDraggedFrom(square)
    if (onPieceSelect) onPieceSelect(square)

    // Set up drag transfer
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', '')

    // Firefox drag image fix: Use piece image directly
    const pieceElement = e.target.closest('.chess-piece')
    const pieceImage = pieceElement?.querySelector('.piece-image')

    if (pieceImage) {
      try {
        // Use the piece image directly - simple and reliable
        e.dataTransfer.setDragImage(pieceImage, pieceImage.offsetWidth / 2, pieceImage.offsetHeight / 2)
      } catch (error) {
        // If setDragImage fails for any reason, just continue without it
        console.log('setDragImage failed:', error)
      }
    }
  }

  const handleDrop = (e, rank, file) => {
    e.preventDefault()
    if (!draggedFrom || !onMove) { setDraggedPiece(null); setDraggedFrom(null); return }
    
    const toSquare = coordsToSquare(rank, file)
    if (draggedFrom === toSquare) { setDraggedPiece(null); setDraggedFrom(null); return }

    if (!legalMoves.includes(toSquare)) {
      if (onPieceDeselect) onPieceDeselect()
    } else {
      onMove({ from: draggedFrom, to: toSquare, piece: draggedPiece })
    }
    setDraggedPiece(null); setDraggedFrom(null)
    setAnnotations({ arrows: [], circles: [] }) // Clear annotations on move
    if (onPieceDeselect) onPieceDeselect()
  }

  const handleSquareClick = useCallback((rank, file) => {
    const square = coordsToSquare(rank, file)
    if (onSquareClick) {
      onSquareClick(square, rank, file)
    }
  }, [coordsToSquare, onSquareClick])

  // --- Annotation Handlers ---
  const getSquareFromEvent = useCallback((event) => {
    if (!boardRef.current) return null
    const boardRect = boardRef.current.getBoundingClientRect()
    const x = event.clientX - boardRect.left
    const y = event.clientY - boardRect.top
    const squareSize = boardRect.width / 8
    const file = Math.floor(x / squareSize)
    const rank = Math.floor(y / squareSize)
    const adjustedRank = orientation === 'white' ? rank : 7 - rank
    const adjustedFile = orientation === 'white' ? file : 7 - file
    if (adjustedRank >= 0 && adjustedRank < 8 && adjustedFile >= 0 && adjustedFile < 8) {
      return coordsToSquare(adjustedRank, adjustedFile)
    }
    return null
  }, [orientation, coordsToSquare])

  const handleRightMouseDown = useCallback((event) => {
    if (event.button !== 2) return
    event.preventDefault()
    setIsRightClicking(true)
    const square = getSquareFromEvent(event)
    if (square) {
      setRightClickStart(square)
      setPreviewArrow(null)
    }
  }, [getSquareFromEvent])

  const handleRightMouseUp = useCallback((event) => {
    if (event.button !== 2) return
    event.preventDefault()
    if (!isRightClicking || !rightClickStart) return
    const endSquare = getSquareFromEvent(event)
    if (!endSquare) { setIsRightClicking(false); setRightClickStart(null); return }

    setAnnotations(prev => {
      const newAnnotations = { ...prev }
      if (rightClickStart === endSquare) {
        const existing = newAnnotations.circles.findIndex(c => c.square === endSquare)
        if (existing >= 0) newAnnotations.circles = newAnnotations.circles.filter((_, i) => i !== existing)
        else newAnnotations.circles = [...newAnnotations.circles, { square: endSquare }]
      } else {
        const existing = newAnnotations.arrows.findIndex(a => a.from === rightClickStart && a.to === endSquare)
        if (existing >= 0) newAnnotations.arrows = newAnnotations.arrows.filter((_, i) => i !== existing)
        else newAnnotations.arrows = [...newAnnotations.arrows, { from: rightClickStart, to: endSquare }]
      }
      return newAnnotations
    })
    setIsRightClicking(false); setRightClickStart(null); setPreviewArrow(null)
  }, [isRightClicking, rightClickStart, getSquareFromEvent])

  const handleMouseMove = useCallback((event) => {
    if (!isRightClicking || !rightClickStart) return
    const currentSquare = getSquareFromEvent(event)
    if (currentSquare && currentSquare !== rightClickStart) {
      setPreviewArrow({ from: rightClickStart, to: currentSquare })
    } else {
      setPreviewArrow(null)
    }
  }, [isRightClicking, rightClickStart, getSquareFromEvent])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setAnnotations({ arrows: [], circles: [] })
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // --- Rendering ---
  const activeTheme = BOARD_THEMES[boardTheme] || BOARD_THEMES.classic
  const getBoardGlowClasses = useMemo(() => {
    let classes = 'squares-grid'
    if (boardTheme === 'riftchess') classes += ' riftchess-border'
    if (gameStatus === 'active' && isPlayerInGame && currentTurn) {
      if (currentTurn === 'white') classes += ' glow-bottom-right' // Standard
      else classes += ' glow-top-right'
    }
    return classes
  }, [boardTheme, gameStatus, isPlayerInGame, currentTurn])

  const getChessBoardClasses = useMemo(() => {
    let classes = `chess-board ${teleportGhostPiece ? 'teleport-mode' : ''}`
    if (gameStatus === 'active' && isPlayerInGame && currentTurn) {
        const inverted = orientation === 'black'
        if (currentTurn === 'white') classes += inverted ? ' black-turn' : ' white-turn' 
        else classes += inverted ? ' white-turn' : ' black-turn'
        if (isCurrentPlayerTurn) classes += ' enhanced-glow'
    }
    return classes
  }, [teleportGhostPiece, gameStatus, isPlayerInGame, currentTurn, isCurrentPlayerTurn, orientation])

  const renderSquare = (rank, file) => {
    const square = coordsToSquare(rank, file)
    const piece = board[rank][file]
    const isLight = (rank + file) % 2 === 0
    const isSelected = selectedSquare === square
    const isLegalMove = legalMoves.includes(square)
    
    // Updated Special & Visual Check
    let appliedSpecialType = null
    if (specialSquares.red.find(s => s.position === square)) appliedSpecialType = 'red'
    else if (specialSquares.blue.find(s => s.position === square)) appliedSpecialType = 'blue'
    else if (specialSquares.green.find(s => s.position === square)) appliedSpecialType = 'green'
    
    // Hide blue if piece exists (matching original logic)
    if (appliedSpecialType === 'blue' && piece) appliedSpecialType = null

    const visualEffect = visualEffects.find(e => e.position === square)
    // Promotion filter logic
    let shouldShowEffect = visualEffect
    if (visualEffect?.type === 'promotion') {
      if (piece && piece.toLowerCase() !== 'p') shouldShowEffect = null
    }

    const isGhostEffect = shouldShowEffect?.type === 'ghost'

    // Status Checks
    const isKingInCheckSquare = piece && piece.toLowerCase() === 'k' && (() => {
        const isWhiteKing = piece === piece.toUpperCase()
        return isKingInCheck(board, isWhiteKing)
    })()

    let squareClasses = ['chess-square', isLight ? 'light' : 'dark']
    if (isSelected) squareClasses.push('selected')
    if (isLegalMove) squareClasses.push('legal-move')
    if (appliedSpecialType) squareClasses.push(`special-${appliedSpecialType}`)
    if (isKingInCheckSquare) squareClasses.push('king-in-check')
    
    if (shouldShowEffect) squareClasses.push(`effect-${shouldShowEffect.type}`)
    if (teleportTargets.includes(square)) squareClasses.push('teleport-target')
    if (illegalTeleportSquares.includes(square)) squareClasses.push('illegal-teleport')
    if (propLastMove?.from === square) squareClasses.push('last-move-from')
    if (propLastMove?.to === square) squareClasses.push('last-move-to')

    const squareStyle = {
      backgroundColor: isLight ? activeTheme.lightSquare : activeTheme.darkSquare
    }

    return (
      <div
        key={`${rank}-${file}`}
        className={squareClasses.join(' ')}
        style={squareStyle}
        onClick={() => handleSquareClick(rank, file)}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
        onDrop={(e) => handleDrop(e, rank, file)}
        data-square={square}
      >
        {/* Piece */}
        {piece && (() => {
          const isWhitePiece = piece === piece.toUpperCase()
          const isCurrentTurn = game?.current_turn && ((game.current_turn === 'white' && isWhitePiece) || (game.current_turn === 'black' && !isWhitePiece))
          const isDraggedPiece = draggedFrom === square && draggedPiece === piece
          const isAnimatingFromHere = activeAnimatingMove && activeAnimatingMove.from === square
          const isAnimatingToHere = activeAnimatingMove && activeAnimatingMove.to === square && animatedPiecePosition?.animating

          return (
            <div
              className={`chess-piece ${isWhitePiece ? 'white-piece' : 'black-piece'} ${isGhostEffect ? 'ghost-piece' : ''} ${isCurrentTurn ? 'active-turn' : ''} ${isDraggedPiece ? 'dragging' : ''} ${isAnimatingFromHere || isAnimatingToHere ? 'animating-hidden' : ''}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, rank, file)}
            >
              <img
                src={pieceImages[piece]}
                alt={`${isWhitePiece ? 'White' : 'Black'} ${piece.toLowerCase()}`}
                className="piece-image"
                draggable={false}
              />
            </div>
          )
        })()}

        {/* Ghost Overlay */}
        {isGhostEffect && shouldShowEffect.piece && !piece && (
           <div className={`chess-piece ${shouldShowEffect.piece === shouldShowEffect.piece.toUpperCase() ? 'white-piece' : 'black-piece'} ghost-piece ghost-overlay`}>
             <img src={pieceImages[shouldShowEffect.piece]} className="piece-image" draggable={false} alt=""/>
           </div>
        )}

        {isLegalMove && <div className="legal-move-indicator" />}

        {appliedSpecialType && (
          <div className={appliedSpecialType === 'red' ? 'special-red-icon' : `special-square-icon special-${appliedSpecialType}-icon`}>
            {appliedSpecialType === 'red' && <span className="special-icon skull-icon">💀</span>}
            {appliedSpecialType === 'blue' && <span className="special-icon spiral-icon">✦</span>}
            {appliedSpecialType === 'green' && <span className="special-icon upgrade-icon">↗</span>}
          </div>
        )}

        {showAnnotations && (
          <div className="square-annotation">
            {square.toLowerCase()}
          </div>
        )}
      </div>
    )
  }

  const rankOrder = orientation === 'white' ? [0,1,2,3,4,5,6,7] : [7,6,5,4,3,2,1,0]
  const fileOrder = orientation === 'white' ? [0,1,2,3,4,5,6,7] : [7,6,5,4,3,2,1,0]

  return (
    <div className={allowResize ? 'resizable-board-container' : 'responsive-board-wrapper'} style={allowResize ? { width: boardSize, height: boardSize, '--board-size': `${boardSize}px` } : undefined}>
      <div className="chess-board-container" style={{ width: '100%', height: allowResize ? '100%' : 'auto' }}>
      
      {topPlayer}

      <div
        className={getChessBoardClasses}
        ref={boardRef}
        onMouseDown={handleRightMouseDown}
        onMouseUp={handleRightMouseUp}
        onMouseMove={handleMouseMove}
        onContextMenu={(e) => e.preventDefault()}
        style={{ position: 'relative' }}
      >
        <div className="board-squares">
          <div className={getBoardGlowClasses}>
            {rankOrder.map((rankIndex) => (
              <div key={`rank-${rankIndex}`} className="board-rank">
                {fileOrder.map((fileIndex) => renderSquare(rankIndex, fileIndex))}
              </div>
            ))}
          </div>
        </div>

        {/* Annotation SVG */}
        <svg className="annotation-overlay" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10 }}>
          {annotations.arrows.map((arrow, index) => {
            const fromCoords = squareToCoords(arrow.from); const toCoords = squareToCoords(arrow.to)
            if (!fromCoords || !toCoords) return null
            const fromX = (orientation === 'white' ? fromCoords[1] + 0.5 : 7 - fromCoords[1] + 0.5) * 12.5
            const fromY = (orientation === 'white' ? fromCoords[0] + 0.5 : 7 - fromCoords[0] + 0.5) * 12.5
            const toX = (orientation === 'white' ? toCoords[1] + 0.5 : 7 - toCoords[1] + 0.5) * 12.5
            const toY = (orientation === 'white' ? toCoords[0] + 0.5 : 7 - toCoords[0] + 0.5) * 12.5
            
            return (
              <g key={`arrow-${index}`}>
                <defs>
                  <marker id={`arrowhead-${index}`} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="rgba(0, 123, 255, 0.3)" />
                  </marker>
                </defs>
                <line x1={`${fromX}%`} y1={`${fromY}%`} x2={`${toX}%`} y2={`${toY}%`} stroke="rgba(0, 123, 255, 0.3)" strokeWidth="8" markerEnd={`url(#arrowhead-${index})`} />
              </g>
            )
          })}
          {previewArrow && (() => {
             const fromCoords = squareToCoords(previewArrow.from); const toCoords = squareToCoords(previewArrow.to)
             if (!fromCoords || !toCoords) return null
             const fromX = (orientation === 'white' ? fromCoords[1] + 0.5 : 7 - fromCoords[1] + 0.5) * 12.5
             const fromY = (orientation === 'white' ? fromCoords[0] + 0.5 : 7 - fromCoords[0] + 0.5) * 12.5
             const toX = (orientation === 'white' ? toCoords[1] + 0.5 : 7 - toCoords[1] + 0.5) * 12.5
             const toY = (orientation === 'white' ? toCoords[0] + 0.5 : 7 - toCoords[0] + 0.5) * 12.5
             return (
               <g>
                 <defs><marker id="preview-arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="rgba(0, 123, 255, 0.2)" /></marker></defs>
                 <line x1={`${fromX}%`} y1={`${fromY}%`} x2={`${toX}%`} y2={`${toY}%`} stroke="rgba(0, 123, 255, 0.2)" strokeWidth="8" markerEnd="url(#preview-arrowhead)" strokeDasharray="5,5" />
               </g>
             )
          })()}
          {annotations.circles.map((circle, index) => {
             const coords = squareToCoords(circle.square)
             if (!coords) return null
             const cx = (orientation === 'white' ? coords[1] + 0.5 : 7 - coords[1] + 0.5) * 12.5
             const cy = (orientation === 'white' ? coords[0] + 0.5 : 7 - coords[0] + 0.5) * 12.5
             return <circle key={`circle-${index}`} cx={`${cx}%`} cy={`${cy}%`} r="5.5%" fill="none" stroke="rgba(0, 123, 255, 0.3)" strokeWidth="10" />
          })}
        </svg>

        {ghostPosition && teleportGhostPiece && pieceImages[teleportGhostPiece] && (
           <div className="teleport-ghost" style={{ left: ghostPosition.x, top: ghostPosition.y }}>
             <img src={pieceImages[teleportGhostPiece]} className="piece-image" draggable={false} alt="" />
           </div>
        )}

        {animatedPiecePosition && pieceImages[animatedPiecePosition.piece] && (
          <div className="animated-piece" style={{ position: 'absolute', left: animatedPiecePosition.left, top: animatedPiecePosition.top, width: animatedPiecePosition.width, height: animatedPiecePosition.height, transition: 'left 0.1s ease-out, top 0.1s ease-out', zIndex: 1000, pointerEvents: 'none' }}>
             <div className={`chess-piece ${animatedPiecePosition.piece === animatedPiecePosition.piece.toUpperCase() ? 'white-piece' : 'black-piece'}`}>
                <img src={pieceImages[animatedPiecePosition.piece]} className="piece-image" draggable={false} alt="" />
             </div>
          </div>
        )}

        {allowResize && <BoardResizer onSizeChange={setBoardSize} initialSize={400} minSize={330} maxSize={800} />}
      </div>
      
      {bottomPlayer}
      
      </div>
    </div>
  )
}

export default ChessBoard
